/**
 * 권한 관련 전역 store (각 페이지 컴포넌트에선 권한 전역 Store만 보도록)
 * - 로그인 여부
 * - 권한 상태
 *  - 로그인 여부
 *  - PLAYNC 전환 여부
 *  - 사업장 유무
 *  - 파트너 PC방 가입 여부
 * - 현재 선택한 사업장 정보
 */

import { createWithEqualityFn } from "zustand/traditional";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { LoginICafeInfoType } from "@features/auth/model/loginModel";
import type { ICafeListType } from "@features/account/model/response/iCafeList.response";
import type { AuthType } from "@core/types/authType";

/*
  권한
  - LOGOUT_USER(로그아웃 상태)
  - NCFZ_USER_0(NCFZ 계정으로 로그인 - 계정 전환 필요 상태)
  - NCFZ_USER_1(NCFZ 계정으로 로그인 - 계정 전환 완료 상태)
  - PLAYNC_USER_0(PLAYNC 계정으로 로그인 - PC방 등록하지 않은 상태)
  - PLAYNC_USER_1(PLAYNC 계정으로 로그인 - PC방 등록 후 승인 대기 상태)
  - PLAYNC_USER_2(PLAYNC 계정으로 로그인 - PC방 등록 승인 완료)
*/

export interface IAuthStore {
  isLogin: boolean; // 로그인 여부
  isLoginLoading: boolean; // 로그인 로딩 여부
  auth: AuthType | null; // 권한 상태
  primaryICafeId: string | null; // 대표 PC방 iCafeId
  iCafeList: ICafeListType | null; // PC방 목록
  isWithdrawn: boolean; // PC방 전체 삭제 여부
  loginICafeInfo: LoginICafeInfoType | null; // 로그인 정보
  isPlayncUser: () => boolean;
  setIsLogin: (isLogin: boolean) => void;
  setIsLoginLoading: (isLoginLoading: boolean) => void;
  setAuth: (auth: AuthType) => void;
  setPrimaryICafeId: (primaryICafeId: string | null) => void;
  setPlayncICafeList: (iCafeList: ICafeListType | null) => void;
  setIsWithdrawn: (isWithdrawn: boolean) => void;
  setLoginICafeInfo: (loginICafeInfo: LoginICafeInfoType | null) => void;
}

export const useAuthStore = createWithEqualityFn<IAuthStore>()(
  devtools(
    immer((set, get) => ({
      isLogin: false,
      isLoginLoading: true,
      auth: null,
      primaryICafeId: null,
      iCafeList: null,
      isWithdrawn: false,
      loginICafeInfo: null,
      isPlayncUser: () => ["PLAYNC_USER_0", "PLAYNC_USER_1", "PLAYNC_USER_2"].includes(get()?.auth ?? ""),
      setIsLogin: (isLogin: boolean) => set({ isLogin }),
      setIsLoginLoading: (isLoginLoading: boolean) => set({ isLoginLoading }),
      setAuth: (auth: AuthType | null) => set({ auth }),
      setPrimaryICafeId: (primaryICafeId: string | null) => set({ primaryICafeId }),
      setPlayncICafeList: (iCafeList: ICafeListType | null) => set({ iCafeList }),
      setIsWithdrawn: (isWithdrawn: boolean) => set({ isWithdrawn }),
      setLoginICafeInfo: (loginICafeInfo: LoginICafeInfoType | null) => set({ loginICafeInfo }),
    })),
    {
      name: "ncfz-authStore",
      enabled: process.env.NODE_ENV === "development", // 개발 환경에서만 활성화
    },
  ),
);
